// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - USER TRACKING MANAGER
// Tracks purity points, beta phase, $DISTIL rewards, and Toilet Mode stats
// ═══════════════════════════════════════════════════════════════════════════

import { BETA_LAUNCH_CONFIG, getCurrentMultiplier, calculatePoints } from '../constants';

// ── Key helpers ────────────────────────────────────────────────────────────
// All real-wallet data is scoped to the connected address.
// Demo data uses a separate flat key so a demo->wallet transition can safely
// wipe ONLY demo data without touching any real wallet's tracking.
const DEMO_TRACKING_KEY = 'distil_user_tracking_demo';

function getStorageKey(walletAddress) {
  if (!walletAddress) return DEMO_TRACKING_KEY;
  return `distil_user_tracking_${walletAddress}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// DATA STRUCTURE
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_USER_DATA = {
  // Core tracking (always visible)
  totalPurityPoints: 0,
  totalPurifications: 0,
  totalObjectsCleaned: 0,
  
  // Beta tracking (visible when launched)
  betaPhase: null, // 'private_beta' or 'public_beta'
  betaStartDate: null,
  totalDistilEarned: 0,
  
  // 🚽 Toilet Mode Stats (NEW)
  toiletModePurifications: 0,
  spinWheelWins: 0,
  flushFrenzyHighScore: 0,
  
  // History
  purificationHistory: [],
  
  // Metadata
  firstPurification: null,
  lastPurification: null,
  version: '1.0',
};

// ═══════════════════════════════════════════════════════════════════════════
// CORE FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user tracking data for a specific wallet (or demo if no address)
 * @param {string|null} walletAddress
 * @returns {Object} User tracking data
 */
export function getUserTrackingData(walletAddress = null) {
  try {
    const key = getStorageKey(walletAddress);
    const stored = localStorage.getItem(key);
    if (!stored) return { ...DEFAULT_USER_DATA };
    const data = JSON.parse(stored);
    return { ...DEFAULT_USER_DATA, ...data };
  } catch (error) {
    console.error('Error loading user tracking data:', error);
    return { ...DEFAULT_USER_DATA };
  }
}

/**
 * Save user tracking data for a specific wallet
 * @param {Object} data - User tracking data
 * @param {string|null} walletAddress
 */
export function saveUserTrackingData(data, walletAddress = null) {
  try {
    const key = getStorageKey(walletAddress);
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving user tracking data:', error);
  }
}

/**
 * Track a purification event
 * @param {number} objectCount - Number of objects purified
 * @param {number} purityEarned - Purity score earned
 * @param {number} storageRebate - SUI storage rebate amount
 * @returns {Object} Updated user data with rewards
 */
export function trackPurification(objectCount, purityEarned, storageRebate = 0, walletAddress = null) {
  const userData = getUserTrackingData(walletAddress);
  const timestamp = new Date().toISOString();
  
  // Calculate base points
  const basePoints = objectCount * BETA_LAUNCH_CONFIG.basePointsPerObject;
  
  // Get current multiplier
  const multiplier = getCurrentMultiplier();
  
  // Calculate actual points earned (with multiplier if beta launched)
  const pointsEarned = Math.floor(basePoints * multiplier);
  
  // Calculate $DISTIL tokens (1 point = 1 token when airdrop happens)
  const distilEarned = pointsEarned;
  
  // Update totals
  userData.totalPurityPoints += pointsEarned;
  userData.totalPurifications += 1;
  userData.totalObjectsCleaned += objectCount;
  userData.totalDistilEarned += distilEarned;
  userData.lastPurification = timestamp;
  
  // Set first purification if this is the first
  if (!userData.firstPurification) {
    userData.firstPurification = timestamp;
  }
  
  // Set beta phase if launched and not already set
  if (BETA_LAUNCH_CONFIG.isLaunched && !userData.betaPhase) {
    userData.betaPhase = BETA_LAUNCH_CONFIG.phase;
    userData.betaStartDate = timestamp;
  }
  
  // Add to history
  const historyEntry = {
    timestamp,
    objectCount,
    purityEarned,
    storageRebate,
    pointsEarned,
    distilEarned,
    multiplier,
    betaPhase: BETA_LAUNCH_CONFIG.isLaunched ? BETA_LAUNCH_CONFIG.phase : null,
  };
  
  userData.purificationHistory.push(historyEntry);
  
  // Keep only last 100 entries to avoid storage bloat
  if (userData.purificationHistory.length > 100) {
    userData.purificationHistory = userData.purificationHistory.slice(-100);
  }
  
  // Save updated data
  saveUserTrackingData(userData, walletAddress);
  
  console.log('📊 Purification tracked:', {
    objects: objectCount,
    points: pointsEarned,
    multiplier: multiplier,
    distil: distilEarned,
  });
  
  return {
    userData,
    rewards: {
      pointsEarned,
      distilEarned,
      multiplier,
      basePoints,
    },
  };
}

/**
 * 🚽 Track Toilet Mode specific stats
 * @param {string} action - 'purification' | 'spinWheelWin' | 'flushFrenzyScore'
 * @param {number} value - Value to track (count or score)
 */
export function trackToiletModeStats(action, value = 1, walletAddress = null) {
  const userData = getUserTrackingData(walletAddress);
  
  switch (action) {
    case 'purification':
      userData.toiletModePurifications = (userData.toiletModePurifications || 0) + value;
      console.log(`🚽 Toilet purifications: ${userData.toiletModePurifications}`);
      break;
      
    case 'spinWheelWin':
      userData.spinWheelWins = (userData.spinWheelWins || 0) + value;
      console.log(`🎰 SpinWheel wins: ${userData.spinWheelWins}`);
      break;
      
    case 'flushFrenzyScore':
      // Only update if this is a new high score
      if (value > (userData.flushFrenzyHighScore || 0)) {
        userData.flushFrenzyHighScore = value;
        console.log(`⚡ New Flush Frenzy high score: ${userData.flushFrenzyHighScore}`);
      }
      break;
      
    default:
      console.warn(`Unknown Toilet Mode action: ${action}`);
      return;
  }
  
  saveUserTrackingData(userData, walletAddress);
}

/**
 * Get user statistics for display
 * @returns {Object} User stats
 */
export function getUserStats(walletAddress = null) {
  const userData = getUserTrackingData(walletAddress);
  const multiplier = getCurrentMultiplier();
  
  return {
    // Core stats (always visible)
    totalPoints: userData.totalPurityPoints,
    totalPurifications: userData.totalPurifications,
    totalObjectsCleaned: userData.totalObjectsCleaned,
    
    // Beta stats (only if launched)
    isBetaLaunched: BETA_LAUNCH_CONFIG.isLaunched,
    betaPhase: userData.betaPhase || BETA_LAUNCH_CONFIG.phase,
    currentMultiplier: multiplier,
    totalDistilEarned: userData.totalDistilEarned,
    
    // 🚽 Toilet Mode stats
    toiletModePurifications: userData.toiletModePurifications || 0,
    spinWheelWins: userData.spinWheelWins || 0,
    flushFrenzyHighScore: userData.flushFrenzyHighScore || 0,
    
    // Metadata
    firstPurification: userData.firstPurification,
    lastPurification: userData.lastPurification,
  };
}

/**
 * Reset DEMO tracking data only (called on demo->wallet transition)
 * Never touches real wallet data.
 */
export function resetUserTracking() {
  localStorage.removeItem(DEMO_TRACKING_KEY);
  // Also clear the old unscoped key from before this fix, one-time migration
  localStorage.removeItem('distil_user_tracking');
}

/**
 * Reset tracking data for a specific wallet (admin/testing use only)
 * @param {string} walletAddress
 */
export function resetWalletTracking(walletAddress) {
  if (!walletAddress) return;
  localStorage.removeItem(getStorageKey(walletAddress));
}

/**
 * Get purification history
 * @param {number} limit - Number of entries to return
 * @returns {Array} Purification history
 */
export function getPurificationHistory(limit = 10, walletAddress = null) {
  const userData = getUserTrackingData(walletAddress);
  return userData.purificationHistory.slice(-limit).reverse();
}

/**
 * Format points for display
 * @param {number} points - Points to format
 * @returns {string} Formatted points
 */
export function formatPoints(points) {
  return points.toLocaleString('en-US');
}

/**
 * Format $DISTIL amount for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount
 */
export function formatDistilAmount(amount) {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}M`;
  }
  if (amount >= 1000) {
    return `${(amount / 1000).toFixed(2)}K`;
  }
  return amount.toLocaleString('en-US');
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORT DEFAULT
// ═══════════════════════════════════════════════════════════════════════════

export default {
  getUserTrackingData,
  saveUserTrackingData,
  trackPurification,
  trackToiletModeStats,
  getUserStats,
  resetUserTracking,
  getPurificationHistory,
  formatPoints,
  formatDistilAmount,
};
